'use client';

import { ALL_NAV_ITEMS } from '@/constants/navigation';
import { APP_MANAGER_ACCESS_ROLES } from '@/constants/config';
import { INavItem } from '@/types';
import { isFeatureEnabled } from '@/constants/feature-flags';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@/components/Auth';
import { useMemo } from 'react';
import { useRouter, usePathname } from 'next/navigation';

// ================================
// Module-level nav item lists
// ================================
/**
 * These lists are computed once at module load time (not inside the hook) so
 * they are stable references that do not cause unnecessary re-renders.
 *
 * The filtering pipeline is:
 *   ALL_NAV_ITEMS  →  FILTERED_NAV_ITEMS  →  SALESREP_NAV_ITEMS
 *                                         →  MANAGER_NAV_ITEMS
 */

/**
 * All navigation items that pass their feature flag check.
 *
 * Items with an empty `featureFlag` array are always included — they are
 * considered unconditionally enabled (e.g. Sign Out).
 *
 * Items with one or more flags are only included when every flag evaluates
 * to `true` via `isFeatureEnabled`.
 */
const FILTERED_NAV_ITEMS = ALL_NAV_ITEMS.filter((item) => {
  if (item.featureFlag.length === 0) return item; // No feature flag means always enabled
  return isFeatureEnabled(item.featureFlag);
});

/**
 * Subset of `FILTERED_NAV_ITEMS` visible to the Sales Representative role.
 *
 * Includes:
 * - Items with no role restriction (`roles: []`) — shared across all roles.
 * - Items that explicitly list `'rep'` in their `roles` array.
 */
const SALESREP_NAV_ITEMS = FILTERED_NAV_ITEMS.filter((item) => {
  if (item.roles.length === 0) return item; // No roles means always available
  return item.roles.includes('rep');
});

/**
 * Subset of `FILTERED_NAV_ITEMS` visible to manager-level roles.
 *
 * Includes:
 * - Items with no role restriction (`roles: []`) — shared across all roles.
 * - Items whose `roles` array contains at least one entry that appears in
 *   `APP_MANAGER_ACCESS_ROLES` (e.g. `dm`, `nsd`, `rbd`, etc.).
 */
const MANAGER_NAV_ITEMS = FILTERED_NAV_ITEMS.filter((item) => {
  if (item.roles.length === 0) return item; // No roles means always available
  return item.roles.some((role) => APP_MANAGER_ACCESS_ROLES.includes(role));
});

// ================================
// Hook
// ================================

/**
 * Custom hook for navigation-related routing.
 *
 * Responsibilities:
 * - Determines which navigation items to display based on the authenticated
 *   user's role.
 * - Marks the currently active route so the UI can highlight the correct item.
 * - Provides `onClick` handlers that call `router.push` for standard routes
 *   and `logout()` for the sign-out item.
 *
 * Returns:
 * - `activeRoute` — the current pathname string.
 * - `navItems`    — the resolved, ready-to-render `INavItem[]` list.
 */
export function useNavigationRouting() {
  // ================================
  // Dependencies
  // ================================
  const router = useRouter();
  const pathname = usePathname();
  const { user, logout } = useAuth();

  // ================================
  // Resolved nav items
  // ================================
  /**
   * Derives the navigation item list for the current user.
   *
   * Steps:
   * 1. Return an empty list immediately if no user is authenticated.
   * 2. Pick the role-appropriate pre-filtered list (`SALESREP_NAV_ITEMS`
   *    or `MANAGER_NAV_ITEMS`).
   * 3. Apply a secondary role filter so items that require a specific role
   *    are only shown to users who possess that role.
   * 4. Map each raw nav constant into the `INavItem` shape expected by the UI,
   *    attaching `isActive` and `onClick` at this point.
   *
   * Memoized on `[user, pathname, router, logout]` so it only recomputes when
   * one of those values actually changes.
   */
  const navItems = useMemo<INavItem[]>(() => {
    if (!user) return [];

    // Select navigation items based on user role
    const roleNavItems = user.role === 'rep' ? SALESREP_NAV_ITEMS : MANAGER_NAV_ITEMS;

    // Map to INavItem structure
    return roleNavItems
      .filter((item) => {
        // If no roles specified, item is available to all users
        if (item.roles.length === 0) return true;
        // Otherwise, check if user's role is included in the item's roles
        return item.roles.includes(user.role);
      })
      .map((item) => ({
        id: item.route,
        label: item.label,
        icon: item.icon,
        // Highlight this item when its route exactly matches the current pathname.
        isActive: pathname === item.route,
        onClick: () => {
          if (item.route === ROUTES.LOGOUT) {
            // The logout route is handled by the auth system, not the router.
            logout();
          } else {
            router.push(item.route);
          }
        },
      }));
  }, [user, pathname, router, logout]);

  // ================================
  // Return
  // ================================
  return {
    /** The current URL pathname — consumers can use this to derive active state independently. */
    activeRoute: pathname,
    /** The resolved navigation items ready for rendering. */
    navItems,
  };
}
